import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import "./FeedbackModal.scss";
import { Box, Button, CircularProgress, FormControlLabel, Paper, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import Header from "../../layout/header/Header";
import approveImage from "../../../src/assets/icons/approve-image.svg";
import rejectImage from "../../../src/assets/icons/reject-image.svg";
import { useTranslation } from "react-i18next";
import { COMPARE_VALUE, UNIT_BLOCKING_STATUS } from "../../config/constant";
import { useFormik } from "formik";
import { useParams } from "react-router-dom";
import SnackbarAlert from "../../shared-components/snackbar-alert/SnackbarAlert";
import { useDispatch } from "react-redux";
import { getApprovalStatus, updateApprovalStatus } from "../voucher-form/voucher-form.slice";
import { route } from "../../services/apiRoutes";
import NotFound from "../not-found/NotFound";
import Loader from "../../components/Loader";

const UnitApprovalThankYou = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useParams();
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [snackbarConfig, setSnackbarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const isApproved = approvalStatus === UNIT_BLOCKING_STATUS.APPROVED;
  const isApprovedOrRejected = approvalStatus === UNIT_BLOCKING_STATUS.APPROVED || approvalStatus === UNIT_BLOCKING_STATUS.REJECTED;

  const formik = useFormik({
    initialValues: {
      decision: UNIT_BLOCKING_STATUS.APPROVED,
      reason: "",
    },
    validate: (values) => {
      const errors = {};
      if (
        values.decision === UNIT_BLOCKING_STATUS.REJECTED &&
        !values.reason.trim()
      ) {
        errors.reason = t("approveUnitThankYou.reasonRequired");
      }
      return errors;
    },

    onSubmit: (values) => {
      const payload = {
        token: token.token,
        action: values.decision === UNIT_BLOCKING_STATUS.APPROVED ? "Approve" : "Reject",
        ...(values.reason && { reason: values.reason }),
      };
      setIsSubmitting(true);
      dispatch(updateApprovalStatus({ path: route.UPDATE_APPROVAL_STATUS, payload }))
        .unwrap()
        .then(() => setApprovalStatus(values.decision))
        .catch(handleError)
        .finally(() => setIsSubmitting(false));
    }
  });

  useEffect(() => {
    if (!token?.token) {
      setIsLoading(false);
      return;
    }
    fetchApprovalStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchApprovalStatus = () => {
    setIsLoading(true);
    dispatch(getApprovalStatus(token.token))
      .unwrap()
      .then((res) => setApprovalStatus(res?.response?.data?.status))
      .catch(handleFetchError)
      .finally(() => setIsLoading(false));
  };

  const handleSnackbarClose = () => {
    setSnackbarConfig({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleError = (error) => {
    setSnackbarConfig({
      open: true,
      message: error?.errors?.message,
      severity: "error",
    });
  };

  const handleFetchError = (error) => {
    setHasError(true);
    if (error?.errors?.message === UNIT_BLOCKING_STATUS.EXPIRED) {
      setApprovalStatus(UNIT_BLOCKING_STATUS.EXPIRED);
    }
    handleError(error);
  };

  if (isLoading) {
    return <Loader isLoading={true} />
  }

  if (approvalStatus === UNIT_BLOCKING_STATUS.EXPIRED || !token?.token) {
    return <NotFound />
  }

  const renderApprovedOrRejectedScreen = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "calc(100vh - 90px)", // adjust if header height differs
        textAlign: "center",
      }}
    >
      <Box
        component="img"
        src={isApproved ? approveImage : rejectImage}
        alt={isApproved ? "approved" : "rejected"}
        sx={{
          maxWidth: "80%",
          height: "auto",
          mb: 5,
        }}
      />

      <Typography
        sx={{
          fontSize: { xs: "20px", sm: "25px" },
          fontWeight: 600,
          color: "#1C252E",
          mb: 1,
        }}
      >
        {isApproved ? t("approveUnitThankYou.approved") : t("approveUnitThankYou.rejected")}
      </Typography>

      <Typography
        sx={{
          fontSize: "18px",
          color: "#1C252E",
          lineHeight: "22px",
          maxWidth: "600px",
        }}
      >
        {isApproved
          ? t("approveUnitThankYou.approvedMessage")
          : t("approveUnitThankYou.rejectedMessage")}
      </Typography>
    </Box>
  );

  const renderApprovalForm = () => (
    <Box component='form' onSubmit={formik.handleSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pt: 8,
        px: 2,
        minHeight: "calc(100vh - 152px)",
        gap: 4,
        mx: { xs: 4, sm: 8 }
      }}
    >
      <Typography sx={{ fontWeight: 500, fontSize: "24px" }}> {t("approveUnitThankYou.title")}</Typography>
      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 752,
          p: 3,
          border: "1px solid #DADADA",
          borderRadius: 2,
          mb: 8
        }}
      >
        <Typography sx={{ fontSize: "14px", fontWeight: "500", mb: '8px' }}>
          {t("approveUnitThankYou.selectDecision")}
        </Typography>
        <RadioGroup
          row
          value={formik.values.decision}
          onChange={(e) => {
            formik.setFieldValue("decision", e.target.value);
          }}
          sx={{
            "& .MuiFormControlLabel-label": {
              fontSize: "14px",
              fontWeight: 400,
            },
          }}
        >
          <FormControlLabel
            value={UNIT_BLOCKING_STATUS.APPROVED}
            control={<Radio size="small" />}
            label={t("approveUnitThankYou.approved")}
          />
          <FormControlLabel
            value={UNIT_BLOCKING_STATUS.REJECTED}
            control={<Radio size="small" />}
            label={t("approveUnitThankYou.rejected")}
          />
        </RadioGroup>
        <Typography sx={{ fontSize: "14px", fontWeight: "500", mb: '10px', mt: '25px' }}>
          {t("commonLabels.reason")} {formik.values.decision === UNIT_BLOCKING_STATUS.REJECTED && <span style={{ color: "red" }}>*</span>}
        </Typography>
        <TextField
          fullWidth
          placeholder={t("approveUnitThankYou.reasonPlaceholder")}
          multiline
          rows={4}
          value={formik.values.reason}
          onChange={(e) => formik.setFieldValue("reason", e.target.value)}
          inputProps={{ maxLength: 500 }}
          helperText={
            formik.touched.reason && formik.errors.reason
              ? formik.errors.reason
              : `${formik.values.reason?.length ?? 0}/500 characters`
          }
          error={formik.touched.reason && Boolean(formik.errors.reason)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              "& fieldset": {
                borderColor: "#919EAB33",
              },
              "&:hover fieldset": {
                borderColor: "#919EAB33",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#8594a333",
              },
            },
          }}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, }}>
          <Button
            variant="contained"
            type="submit"
            disabled={hasError || isSubmitting}
            sx={{
              width: 206,
              height: 48,
              borderRadius: 2,
              bgcolor: "#1A407D",
              fontSize: 15,
              fontWeight: 700,
              textTransform: "none",
              "&:hover": {
                bgcolor: "#092552",
              },
            }}
          >
            {isSubmitting ? <CircularProgress size={20} color="inherit" /> : t("commonLabels.submit")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );

  return (
    <Box className="hideHamburger">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Header logo={COMPARE_VALUE?.Puravankara} />
      {isApprovedOrRejected ? renderApprovedOrRejectedScreen() : renderApprovalForm()}

      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default UnitApprovalThankYou;
